import { html, TemplateResult } from '../lit_component/lit_component';
import { fullstory } from '../log/fullstory';
import {
  announceLMSAlert,
  canUsePostMessage,
  showLMSAlert,
} from '../lti/tool-provider-post-message';
import 'derivita/third_party/material/mwc/packages/snackbar/mwc-snackbar';
import 'derivita/third_party/material/mwc/packages/button/mwc-button';
import 'derivita/third_party/material/mwc/packages/icon-button/mwc-icon-button';
import { Snackbar as MWCSnackbar } from 'derivita/third_party/material/mwc/packages/snackbar/mwc-snackbar';

// MWC Snackbar requires timeoutMs in the range 4000–10000, or -1 to disable.
export enum TOAST_DURATION {
  DEFAULT = 4000,
  LONG = 10000,
  PERSISTENT = -1,
}

/** Lazily creates an mwc-snackbar on document.body if one doesn't exist. */
function getOrCreateSnackbar(): MWCSnackbar {
  let snackbar = document.querySelector('mwc-snackbar') as MWCSnackbar | null;
  if (!snackbar) {
    snackbar = document.createElement('mwc-snackbar') as MWCSnackbar;
    document.body.appendChild(snackbar);
  }
  return snackbar;
}

export function toast(
  message: string,
  isError = false,
  timeout: number = TOAST_DURATION.DEFAULT,
  actionHandler?: () => void,
  actionText?: string
) {
  if (canUsePostMessage()) {
    showLMSAlert(isError ? 'error' : 'success', message);
  } else {
    const mwcSnackbar = getOrCreateSnackbar();
    mwcSnackbar.labelText = message;
    mwcSnackbar.timeoutMs = timeout;

    // Clear any previous action button
    const existingAction = mwcSnackbar.querySelector('[slot="action"]');
    if (existingAction) {
      existingAction.remove();
    }

    if (actionHandler && actionText) {
      const actionButton = document.createElement('mwc-button');
      actionButton.setAttribute('slot', 'action');
      actionButton.textContent = actionText;
      actionButton.addEventListener('click', actionHandler, { once: true });
      mwcSnackbar.appendChild(actionButton);
    }

    mwcSnackbar.open = true;
  }

  if (isError) {
    fullstory.displayedRuntimeError(message);
  }
}

/**
 * Similar to `toast` but is invisible and only announced to screenreaders. However,
 * it does not support an action.  It is explicitly for announcing things only.
 */
export function srAnnounce(message: string) {
  if (canUsePostMessage()) {
    announceLMSAlert(message);
  } else {
    const alertContainer = document.getElementById('screenreader-alerts');
    if (alertContainer) {
      alertContainer.textContent = message;
    }
  }
}

export function getMWCToastTemplate(
  message: string,
  isDismissible = true,
  isPersistent = false,
  actionText?: string,
  open?: boolean,
  closedFunction?: (e: CustomEvent) => void
): TemplateResult {
  const toast = html`<mwc-snackbar
    ?open=${!!open}
    labelText=${message}
    timeoutMs=${isPersistent ? '-1' : '5000'}
    @MDCSnackbar:closed=${(e: CustomEvent) =>
      handleToastClosed(e, closedFunction)}>
    ${actionText
      ? html` <mwc-button slot="action">${actionText}</mwc-button>`
      : ''}
    ${isDismissible
      ? html`<mwc-icon-button icon="close" slot="dismiss"></mwc-icon-button>`
      : ''}
  </mwc-snackbar>`;
  return toast;
}

export function showToast() {
  const toastEl = document.getElementsByTagName('mwc-snackbar')[0];
  if (toastEl instanceof MWCSnackbar) {
    toastEl.open = true;
  }
}

export function hideToast() {
  const toastEl = document.getElementsByTagName('mwc-snackbar')[0];
  if (toastEl instanceof MWCSnackbar) {
    toastEl.open = false;
  }
}

export function getToastEl(): MWCSnackbar | undefined {
  const toastEl = document.getElementsByTagName('mwc-snackbar')[0];
  if (toastEl instanceof MWCSnackbar) {
    return toastEl;
  }
}

function handleToastClosed(
  e: CustomEvent,
  closedFunction?: (e: CustomEvent) => void
) {
  hideToast();
  if (closedFunction) {
    closedFunction(e);
  }
}
